/**
 * Redux middleware: cascade a full logout when the user's session is dead.
 *
 * Three independent triggers, each scoped to avoid logging users out for
 * per-resource permission errors (a 403 from `/api/boosts/info` because the
 * caller is not the listing owner must NOT nuke auth):
 *
 *  1. Liveness thunks in SESSION_LIVENESS_PREFIXES rejecting with 401.
 *     These thunks exist purely to verify the session is alive.
 *  2. Any thunk whose payload carries `code: 'UNAUTHENTICATED'`: only
 *     ever emitted by `requireSupabaseUser` / `requireDualAuth`, so it
 *     unambiguously means the Supabase session is gone.
 *  3. Any rejected thunk where status === 401 AND the payload has the
 *     Sharetribe SDK error shape (`apiErrors[]`, `data.errors[]`, or a
 *     sharetribe `id`). Sharetribe distinguishes 401 (session dead) from
 *     403 (resource-specific forbidden), so 401 with that shape is a
 *     reliable st-* session-death signal.
 */

const SESSION_LIVENESS_PREFIXES = [
  // Sharetribe SDK currentUser fetch: 401 here means st-* token is dead.
  'user/fetchCurrentUser',
  // Cookie-store auth-info read: 401 from this means cookies are corrupt.
  'auth/authInfo',
  // Boot-time Supabase session probe: 401 here means sb-* is dead, even
  // when Sharetribe is still alive (e.g. password changed in another browser
  // wipes every Supabase session).
  'auth/checkSupabase',
];

const SESSION_DEAD_CODE = 'UNAUTHENTICATED';

const isRejectedThunk = action =>
  typeof action?.type === 'string' && action.type.endsWith('/rejected');

const isSessionLivenessRejection = action =>
  isRejectedThunk(action) &&
  SESSION_LIVENESS_PREFIXES.some(p => action.type.startsWith(p));

const rejectionStatus = action =>
  action?.payload?.status ?? action?.payload?.error?.status ?? action?.error?.status;

const errorCode = action =>
  action?.payload?.code ??
  action?.payload?.data?.code ??
  action?.payload?.apiErrors?.[0]?.code ??
  action?.error?.code;

const isSupabaseSessionDead = action =>
  isRejectedThunk(action) && errorCode(action) === SESSION_DEAD_CODE;

const looksLikeSharetribeSdkError = action =>
  action?.payload?.apiErrors?.length > 0 ||
  action?.payload?.data?.errors?.length > 0 ||
  typeof action?.payload?.id === 'string';

const isSharetribeSessionDead = action =>
  isRejectedThunk(action) &&
  rejectionStatus(action) === 401 &&
  looksLikeSharetribeSdkError(action);

export const auth401Middleware = store => next => action => {
  const result = next(action);

  const livenessHit = isSessionLivenessRejection(action);
  const supabaseDead = !livenessHit && isSupabaseSessionDead(action);
  const sharetribeDead = !livenessHit && !supabaseDead && isSharetribeSessionDead(action);

  if (!livenessHit && !supabaseDead && !sharetribeDead) return result;

  if (livenessHit && rejectionStatus(action) !== 401) return result;

  const state = store.getState();
  if (!state.auth?.isAuthenticated) return result;

  setTimeout(() => {
    if (store.getState().auth?.isAuthenticated) {
      const { logoutThunk } = require('../ducks/auth.duck');
      store.dispatch(logoutThunk());
    }
  }, 0);

  return result;
};
